export const isLoggedIn = (state) => {
  return state.authentication.user_data ? true : false;
};

/** Organisation type from /menu/acl or /user/acl (not organisation records). */
export const getAclOrganisationType = (acl) => {
  if (!acl) return null;
  return acl.organisation_type ?? acl.type_of_organisation ?? null;
};

/**
 * Operator account: organisation_type OPERATOR, or a single operator in menu ACL
 * (see prestatiesAanbiedersViewMode.isOperatorPrestatiesView).
 */
export const isOperatorAccount = (acl) => {
  if (!acl) return false;

  if (getAclOrganisationType(acl) === 'OPERATOR') {
    return true;
  }

  const operators = acl.operators;
  if (
    Array.isArray(operators)
    && operators.length === 1
    && operators[0]?.system_id
    && !acl.is_admin
  ) {
    return true;
  }

  return false;
};

export const canEditHubs = (acl) => {
  if (!acl) return false;

  // OPERATOR organisations are never allowed to edit zones,
  // even if they happen to have the MICROHUB_EDIT privilege.
  // Editing is restricted to MUNICIPALITY, OTHER_GOVERNMENT and ADMIN
  // (see EditUser.tsx -> org_types_allowed_to_edit_zones).
  if (isOperatorAccount(acl)) {
    return false;
  }

  const allowedRoles = ['MICROHUB_EDIT'];

  const hasEditPrivilege = Array.isArray(acl.privileges)
    && acl.privileges.some((role) => allowedRoles.indexOf(role) > -1);

  if (!hasEditPrivilege) {
    return false;
  }

  return true;
}

// NL-wide data access: ACL super-admin, ADMIN organisation, or FusionAuth admin role.
export const isAdmin = (state) => {
  if (!state || !state.authentication) return false;

  const acl = state.authentication.user_data?.acl;
  if (acl?.is_admin === true) return true;
  if (getAclOrganisationType(acl) === 'ADMIN') return true;

  const registrations = state.authentication.user_data?.user?.registrations;
  if (!Array.isArray(registrations)) return false;

  return registrations.some((registration) => (
    Array.isArray(registration.roles) && registration.roles.includes('admin')
  ));
}

// Maximum number of municipalities we still enumerate in a
// `/zones?municipalities=GM..,GM..` (or `/zones?zone_ids=..`) query. Above this,
// the upstream server rejects the request with a 502. Accounts with more
// accessible municipalities than this are treated NL-wide (like admins) for zone
// loading and zone-boundary display; their vehicles are scoped by the auth token
// regardless (see createFilterparameters). Kept well below any realistic
// province-sized list so that small/regional accounts keep their precise zones.
export const MAX_ENUMERABLE_MUNICIPALITIES = 25;

export const getEnumerableMunicipalityCodes = (gebieden) => {
  if (!Array.isArray(gebieden)) return [];
  return gebieden
    .map((gebied) => gebied && gebied.gm_code)
    .filter(Boolean);
};

// True when we should treat this account as NL-wide for zone loading/display:
// admins, ADMIN organisations (ACL municipality list is typically empty), or
// non-admin organisations with access to so many municipalities that enumerating
// them in a zones query would 502. An empty `municipalities=` query is rejected
// by the API, so an empty ACL list is treated NL-wide as well.
export const shouldTreatMunicipalitiesAsNlWide = (state) => {
  if (isAdmin(state)) return true;
  const municipalityCodes = getEnumerableMunicipalityCodes(state?.metadata?.gebieden);
  if (municipalityCodes.length === 0) return true;
  return municipalityCodes.length > MAX_ENUMERABLE_MUNICIPALITIES;
};

// Validate if the current authentication state is valid
export const isValidAuthState = (state) => {
  if (!state || !state.authentication || !state.authentication.user_data) {
    return false;
  }

  const userData = state.authentication.user_data;
  
  // Check if token exists
  if (!userData.token) {
    return false;
  }

  // Optional: Add token expiration check if your API provides expiration info
  // if (userData.token_expires && new Date(userData.token_expires) < new Date()) {
  //   return false;
  // }

  return true;
};

// Clear invalid authentication state
export const clearInvalidAuthState = (dispatch) => {
  dispatch({ type: 'CLEAR_USER' });
  
  // Clear any cached data that depends on authentication
  dispatch({ type: 'CLEAR_VEHICLES', payload: null });
  dispatch({ type: 'CLEAR_RENTALS_ORIGINS', payload: null });
  dispatch({ type: 'CLEAR_RENTALS_DESTINATIONS', payload: null });
  
  // Reset metadata to force reload with public data
  dispatch({ type: 'SET_METADATA_LOADED', payload: false });
};
